import {ApiRepository, ValidationError} from '../base/api-repository';
import {action, makeObservable, observable} from 'mobx';
import {UserStore} from '../user/user-store';
import {LabelStore} from '../base/label-store';
import {ViewStore} from '../base/view-store';
import {SessionStore} from '../user/session-store';
import {formatDateRange, rangeFromPeriodId, todayAsISOString} from '../base/dateUtils';
import {Capacitor} from '@capacitor/core';
import {routes} from '../base/routes';
import {RouterStore} from 'mobx-router';
import {RootStore} from '../base/root-store';
import {Category} from '../questions/questions-store';
import {translateComment} from '../questions/questionUtil';
import {CategoriesStore} from '../categories/categories-store';
import {getCategoryName} from '../categories/CategoriesUtil';
import {IAnswerProps, IComment, ILike} from '../../../patterns/components/feedback-answers/feedback-answers';
import {DistributionCounts} from '../../../patterns/components/dashboard-overview-card/dashboard-overview-card';
export type DashboardContext = 'dashboard' | 'email-report';
export type DashboardPageTab = 'team-results' | 'my-results' | 'new-hires';
export type TeamDashboardModal = 'help-feedback' | 'help-overview' | 'help-category' | 'help-company' | 'help-heatmap' | 'help-location' | 'help-service-length' | 'help-role' | 'help-tag' | 'help-questions'| 'help-sendResults';
type SummaryType = | 'dashboard' | 'category' | 'department' | 'tag' | 'role' | 'serviceLength' | 'country' | 'feedback';

export class TeamResultsStore extends ViewStore {
    @observable
    payload?: TeamResultsPayload = undefined;
    @observable
    variables: TeamResultsVariables = new TeamResultsVariables();
    @observable
    categories?: Category[] = undefined;
    @observable
    translateCommentsEnabled: boolean = false;
    @observable
    isTranslationButtonLoading: boolean = false;
    @observable
    isFiltersOpen: boolean = false;
    @observable
    private managerHasSeenComments = new Map<string,boolean>();
    constructor(
        private api: ApiRepository,
        labelStore: LabelStore,
        private userStore: UserStore,
        private categoriesStore: CategoriesStore,
        private sessionStore: SessionStore,
        private router: RouterStore<RootStore>,
    ) {
        super(labelStore);
        this.variables.openTab = 'team-results';
        makeObservable(this);
    }

    @action
    toggleFilters(): void {
        this.isFiltersOpen = !this.isFiltersOpen;
    }

    @action
    onEnter(context: DashboardContext): void {
        this.variables.openTab = 'team-results';
        this.variables.context = context;
        this.variables.shownQuestionCardsAmount = 21;
        this.variables.categoryChoice = 'ALL';
        const defaultPeriod = 30;

        this.variables.periodId = todayAsISOString().concat('_'+ defaultPeriod);
        this.load(async () => {
            await this.refreshPayload();
        });
    }

    @action
    async onTranslateCommentsButtonClicked(): Promise<void> {
        if (!this.translateCommentsEnabled) {
            this.setIsTranslationButtonLoading(true);
            try {
                await this.api.post('/translation/comments', {
                    questionIds: this.getQuestionIdsToTranslateComments(),
                    language: this.userStore.payload!.language,
                    periodId: this.variables.periodId,
                });
                await this.refreshPayload();
            } finally {
                this.setIsTranslationButtonLoading(false);
            }
        }
        this.toggleTranslateComments();
    }

    @action
    toggleTranslateComments(): void {
        this.translateCommentsEnabled = !this.translateCommentsEnabled;
    }

    @action
    onTokenLogin(employeeId: string, businessUnitId?: string, token?: string, tenantId?: string): void {
        this.initiateLogin(token, tenantId, async (): Promise<void> => {
            this.payload = await this.loadDashboard(employeeId, businessUnitId);
            if (Capacitor.getPlatform() === 'web') {
                const redirectToDashboard = this.getRedirectToDashboardUrl();

                history.replaceState({}, '', redirectToDashboard);
            }
        });
    }

    @action
    onDashboardTabChange(openTab: DashboardPageTab): void {
        this.variables.openTab = openTab;
        switch (openTab) {
            case 'new-hires': this.router.goTo(routes.onboarding);
                break;
            case 'my-results': this.router.goTo(routes.myDashboard);
                break;
        }
    }

    @action
    async sendResultsLink(): Promise<void> {
        await this.load(async () => {
            try {
                this.payload = await this.api.post<TeamResultsPayload>(`/dashboard/employee/${this.userStore.payload!.employeeId}/send-results`);
                await this.refreshPayload();
                window.alert(this.label('send-results.sent'));
            } catch (error) {
                this.variables.validationReason = (error as ValidationError).errorMessageCode;
                if (this.variables.validationReason === 'missing-email') {
                    window.alert(this.label('send-results.error'));
                    console.error('Error sending results link:', (error as ValidationError).errorMessageCode);
                }
                console.error('Error sending results link:', (error as ValidationError).errorMessageCode);
            }
        });
    }

    async closeWeeklyRetro(id: string): Promise<void> {
        await this.api.post(`/dashboard/employee/${this.userStore.payload!.employeeId}/weekly-retro/${id}/close`);
        await this.refreshPayload();
    }

    async emailWeeklyRetro(id: string): Promise<void> {
        await this.api.post(`/dashboard/employee/${this.userStore.payload!.employeeId}/weekly-retro/${id}/email`);
        await this.refreshPayload();
    }

    @action
    onCategoryChange(categoryChoice: string): void {
        this.variables.categoryChoice = categoryChoice;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    toggleCategoryChange(categoryChoice: string): void {
        this.variables.categoryChoice = this.variables.categoryChoice === categoryChoice ? undefined : categoryChoice;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onBusinessUnitChange(businessUnitId: string): void {
        this.variables.businessUnitId = businessUnitId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    toggleBusinessUnitChange(businessUnitId: string): void {
        this.variables.businessUnitId = this.variables.businessUnitId === businessUnitId ? undefined : businessUnitId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onEmployeeSegmentChange(employeeSegment: string): void {
        this.variables.employeeSegment = employeeSegment;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onCompareToChange(compareToBusinessUnitId: string): void {
        this.variables.compareToBusinessUnitId = compareToBusinessUnitId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onLocationChange(locationId: string): void {
        this.variables.locationChoice = locationId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    toggleLocationChange(locationId: string): void {
        this.variables.locationChoice = this.variables.locationChoice === locationId ? undefined : locationId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onRoleChange(roleId: string): void {
        this.variables.role = roleId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    toggleRoleChange(roleId: string): void {
        this.variables.role = this.variables.role === roleId ? undefined : roleId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onLengthOfServiceChange(lengthOfServiceId: string): void {
        this.variables.lengthOfService = lengthOfServiceId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    toggleLengthOfServiceChange(lengthOfServiceId: string): void {
        this.variables.lengthOfService = this.variables.lengthOfService === lengthOfServiceId ? undefined : lengthOfServiceId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onQuestionOptionChange(questionId: string): void {
        this.variables.questionId = questionId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onTagChange(tagName: string): void {
        this.variables.tag = tagName;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    toggleTagChange(tagName: string): void {
        this.variables.tag = this.variables.tag === tagName ? undefined : tagName;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onPeriodChange(periodId: string): void {
        this.variables.periodId = periodId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    async openDataDownloadPage(employeeId: string): Promise<void> {
        try {
            document.body.classList.add('waiting');
            const blob = await this.api.getFile(`/dashboard/export/${employeeId}/moticheck-data`);

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = 'moticheck-data.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download data:', error);
        } finally {
            document.body.classList.remove('waiting');
        }
    }

    @action
    async sendManagerReport(employeeId: string, businessUnitId?: string): Promise<void> {
        await this.load(async () => {
            try {
                await this.api.post<TeamResultsPayload>(`/manager/${employeeId}/summary?businessUnitId=${businessUnitId}`);
                window.alert(this.label('dashboard.manager-report-sent'));
            } catch (error) {
                const validationError = error as ValidationError;

                switch (validationError.errorMessageCode) {
                    case 'not-enough-data':
                        window.alert(this.label('dashboard.manager-report-error-data'));
                        break;
                    case 'missing-email':
                        window.alert(this.label('dashboard.manager-report-error-email'));
                        break;
                    default:
                        window.alert(this.label('common.error.details'));
                        break;
                }
            }
        });
    }

    @action
    setCustomDateSelected(selected: boolean): void {
        this.variables.isCustomDate = selected;
    }

    @action
    openOverviewHelpModal(): void {
        this.variables.openModal = 'help-overview';
    }

    @action
    openFeedbackHelpModal(): void {
        this.variables.openModal = 'help-feedback';
    }

    @action
    openCategoryHelpModal(): void {
        this.variables.openModal = 'help-category';
    }

    @action
    openCompanyHelpModal(): void {
        this.variables.openModal = 'help-company';
    }

    @action
    openLocationHelpModal(): void {
        this.variables.openModal = 'help-location';
    }

    @action
    openHeatmapHelpModal(): void {
        this.variables.openModal = 'help-heatmap';
    }

    @action
    openServiceLengthHelpModal(): void {
        this.variables.openModal = 'help-service-length';
    }

    @action
    openRolesHelpModal(): void {
        this.variables.openModal = 'help-role';
    }

    @action
    openTagsHelpModal(): void {
        this.variables.openModal = 'help-tag';
    }

    @action
    openQuestionsHelpModal(): void {
        this.variables.openModal = 'help-questions';
    }

    @action
    openSendResultsHelpModal(): void {
        this.variables.openModal = 'help-sendResults';
    }

    @action
    async toggleLike(commentId: string): Promise<ILike | undefined> {
        const employeeId = this.userStore.payload?.employeeId;

        if (!employeeId) {
            console.warn('No employee ID found in payload');

            return;
        }

        try {
            const response = await this.api.post<ILike>(`/comments/${employeeId}/${commentId}/like`);

            if (!response) {
                console.warn('No response received from like toggle');

                return;
            }

            if (this.variables.questionWithComment?.id === commentId) {
                this.variables.questionWithComment.likes = response;
            }

            return response;
        } catch (e) {
            console.error('Failed to toggle like:', e);
        }
    }


    @action
    async loadLikeData(commentId: string): Promise<ILike | undefined> {
        const employeeId = this.userStore.payload?.employeeId;

        if (!employeeId) {
            return;
        }

        try {
            const result = await this.api.get<ILike>(`/comments/${employeeId}/${commentId}/likes`);

            if (this.variables.questionWithComment?.id === commentId) {
                this.variables.questionWithComment.likes = result;
            }

            return result;
        } catch (e) {
            console.error('Failed to load like data:', e);
        }
    }

    @action
    async addReplyToQuestionComment(
        questionId: string | undefined,
        newReply: string,
    ): Promise<void> {
        if (!questionId || !newReply.trim()) {
            return;
        }

        const employeeId = this.userStore?.payload?.employeeId;

        if (!employeeId) {
            return;
        }

        try {
            await this.api.post<void>(
                `/comments/${employeeId}/reply`,
                { commentId: questionId, replyText: newReply },
            );

            const response = await this.api.get<IComment[]>(`/comments/${employeeId}/${questionId}/replies`);

            if (this.variables.questionWithComment?.id === questionId) {
                this.variables.questionWithComment.comments = response;
                this.variables.questionWithComment.commentCount = response.length;
            }

        } catch (e) {
            console.error('Failed to add and reload comments:', e);
        }
    }

    @action
    async loadRepliesForQuestion(questionId: string): Promise<IComment[]> {
        const employeeId = this.userStore?.payload?.employeeId;

        if (!employeeId) {
            return [];
        }

        try {
            const response = await this.api.get<IComment[]>(
                `/comments/${employeeId}/${questionId}/replies`,
            );

            if (this.variables.questionWithComment?.id === questionId) {
                this.variables.questionWithComment.comments = response;
                this.variables.questionWithComment.commentCount = response.length;
            }

            return response;
        } catch (e) {
            console.error('Failed to load replies for question:', e);

            return [];
        }
    }

    @action
    async deleteReplyToQuestionComment(commentId: string): Promise<void> {
        const employeeId = this.userStore?.payload?.employeeId;

        if (!employeeId) {
            console.warn('No employee ID in session, aborting delete');

            return;
        }

        try {
            await this.api.post(`/comments/${employeeId}/${commentId}/delete`);

            const q = this.variables.questionWithComment;

            if (q) {
                q.comments = q.comments?.filter((c) => c.id !== commentId);
                q.commentCount = q.comments?.length;
            }
        } catch (e) {
            console.error('Failed to delete comment:', e);
        }
    }


    @action
    async editReplyToQuestionComment(editCommentId: string, editText: string): Promise<void> {
        const employeeId = this.userStore?.payload?.employeeId;

        if (!employeeId) {
            console.warn('No employee ID in session, aborting edit');

            return;
        }

        try {
            await this.api.post(`/comments/${employeeId}/edit`, {
                commentId: editCommentId,
                replyText: editText,
            });

            const q = this.variables.questionWithComment;

            if (q) {
                const reply = q.comments?.find((c) => c.id === editCommentId);

                if (reply) {
                    reply.text = editText;
                }
            }
        } catch (e) {
            console.error('Failed to edit comment:', e);
        }
    }

    @action
    closeModal(): void {
        this.variables.openModal = undefined;
    }

    @action
    managerChecksComment(questionId: string, hasSeen: boolean): void {
        this.managerHasSeenComments.set(questionId, hasSeen);

        const managerHasSeenComment: ManagerHasSeenComment = {
            questionId,
            hasSeen,
        };

        this.api.post(`/dashboard/employee/${this.userStore.payload!.employeeId}/manager-has-seen-comment`, managerHasSeenComment);
    }

    @action
    async routeToMentor(question: QuestionCard): Promise<void> {
        const employeeId = this.userStore.payload!.employeeId;

        const displayQuestion = this.label(
            question.barpoint.value.onlyFreeTextAnswers
                ? 'mentor.question.catalog-question.free-text'
                : 'mentor.question.catalog-question',
            {
                question_text: question.questionText,
                ...(question.barpoint.value.onlyFreeTextAnswers
                    ? {}
                    : { score: question.barpoint.value.scorePercentage.toString() }),
            },
        );

        const questionScore = question.barpoint.value.onlyFreeTextAnswers
            ? undefined
            : question.barpoint.value.scorePercentage.toString();

        await this.router.goTo(routes.mentorAskAdvice, {employeeId}, {
            questionText: question.questionText,
            catalogQuestionId: question.catalogQuestionId,
            questionScore,
            filterBusinessUnitId: this.variables.businessUnitId,
            filterEmployeeSegment: this.variables.employeeSegment,
            filterPeriodId: this.variables.periodId,
            filterLocation: this.variables.locationChoice,
            filterRole: this.variables.role,
            filterTag: this.variables.tag,
            filterServiceLength: this.variables.lengthOfService,
            displayQuestion,
            summaryType: undefined,
        });
    }

    @action
    async routeToMentorForSummary(summaryType: SummaryType): Promise<void> {
        const employeeId = this.userStore.payload!.employeeId;

        const displayQuestion = this.createDisplayQuestion(summaryType);

        await this.router.goTo(routes.mentorAskAdvice, {employeeId}, {
            questionText: undefined,
            catalogQuestionId: undefined,
            questionScore: undefined,
            filterBusinessUnitId: this.variables.businessUnitId,
            filterEmployeeSegment: this.variables.employeeSegment,
            filterPeriodId: this.variables.periodId,
            filterLocation: this.variables.locationChoice,
            filterRole: this.variables.role,
            filterTag: this.variables.tag,
            filterServiceLength: this.variables.lengthOfService,
            filterCategory: this.variables.categoryChoice,
            filterQuestionId: this.variables.questionId,
            displayQuestion,
            summaryType,
        });
    }

    translateComment(comment: AnswerWithComment): string {
        return this.translateCommentsEnabled ? translateComment(comment, this.userStore.payload?.language) : comment.text;
    }

    hasSeen(answer: AnswerWithCommentOnQuestionCard): boolean {
        if (this.managerHasSeenComments.has(answer.questionId)) {
            return this.managerHasSeenComments.get(answer.questionId)!;
        } else {
            return answer.hasSeen;
        }
    }

    timeframeFilterLabels(filterOption: FilterOption): string | undefined {
        if (filterOption.periodLength === 7 && filterOption.periodTo === todayAsISOString()) {
            return this.label('common.filter.period.current-week');
        } else if (filterOption.periodLength === 30 && filterOption.periodTo === todayAsISOString()) {
            return this.label('common.filter.period.past-month');
        } else {
            return undefined;
        }
    }

    visibleQuestionCards(): QuestionCard[] {
        const visibleQuestionCards: QuestionCard[] = [];
        const questions = this.payload!.questions!.questions;

        if (questions.length <= this.variables.shownQuestionCardsAmount) {
            return questions;
        } else {
            for (let i = 0; i < this.variables.shownQuestionCardsAmount; i++){
                visibleQuestionCards.push(questions[i]);
            }

            return visibleQuestionCards;
        }
    }

    shouldShowLoadMoreButton(): boolean {
        const questions = this.payload!.questions!.questions;
        const visibleQuestions = this.visibleQuestionCards();

        return questions > visibleQuestions;
    }

    loadMoreQuestionCards(): void {
        const totalNumberOfQuestionCards = this.payload!.questions?.questions.length;

        if (totalNumberOfQuestionCards !== undefined) {
            if (totalNumberOfQuestionCards >= this.variables.shownQuestionCardsAmount + 21) {
                this.variables.shownQuestionCardsAmount = this.variables.shownQuestionCardsAmount + 21;
            } else {
                this.variables.shownQuestionCardsAmount = (totalNumberOfQuestionCards - this.variables.shownQuestionCardsAmount) + this.variables.shownQuestionCardsAmount;
            }
        } else {
            return;
        }
    }

    async refreshPayload(): Promise <void> {
        this.variables.shownQuestionCardsAmount = 21;
        const queryParameters: Record<string, string> = {};

        if (this.variables.categoryChoice !== undefined) {
            queryParameters.category = this.variables.categoryChoice;
        }
        if (this.variables.businessUnitId !== undefined) {
            queryParameters['business-unit'] = this.variables.businessUnitId;
        }
        if (this.variables.compareToBusinessUnitId !== undefined) {
            queryParameters['compare-to-unit'] = this.variables.compareToBusinessUnitId;
        }
        if (this.variables.periodId !== undefined) {
            queryParameters['period-id'] = this.variables.periodId;
        }
        if (this.variables.employeeSegment !== undefined) {
            queryParameters['employee-segment'] = this.variables.employeeSegment;
        }
        if (this.variables.locationChoice !== undefined) {
            queryParameters.location = this.variables.locationChoice;
        }
        if (this.variables.role !== undefined) {
            queryParameters.role = this.variables.role;
        }
        if (this.variables.lengthOfService !== undefined) {
            queryParameters['length-of-service'] = this.variables.lengthOfService;
        }
        if (this.variables.tag !== undefined) {
            queryParameters.tag = this.variables.tag;
        }
        if (this.variables.questionId !== undefined) {
            queryParameters['question-id']= this.variables.questionId;
        }

        const queryParamsString = new URLSearchParams(queryParameters).toString();

        this.payload = await this.api.get<TeamResultsPayload>(`/dashboard/employee/${this.userStore.payload!.employeeId}?${queryParamsString}`);
        await this.categoriesStore.refreshPayload();
        this.setCategories();
    }

    private createDisplayQuestion(summaryType: SummaryType | undefined) {
        const language = this.userStore.payload!.language;
        const getOrDefault = (value: string | undefined, defaultLabel: string): string => value ? value : this.label(defaultLabel);

        const categoryId = this.payload!.filter.categories.find((it) => it.selected)?.id;
        const categoryName = getCategoryName(language, categoryId!, this.categories);
        const location = this.payload!.filter.locations?.find((it) => it.selected)?.id;
        const employeeSegment = this.payload!.filter.employeeSegment?.find((it) => it.selected)?.id;
        const serviceLength = this.payload!.filter.lengthOfService?.find((it) => it.selected)?.id;
        const businessUnit = this.payload?.filter?.businessUnits.find((it) => it.selected)?.label;
        const periodId = this.variables.periodId;
        const role = this.payload?.filter?.roles?.find((it) => it.selected)?.id;
        const question = this.payload?.filter?.questionId?.find((it) => it.selected)?.label;
        const tag = this.payload?.filter?.tags?.find((it) => it.selected)?.id;

        const commonData = {
            category: getOrDefault(categoryName, 'common.filter.company.ALL'),
            location: getOrDefault(this.label(`common.location.${location}`), 'common.filter.company.ALL'),
            period: getOrDefault(formatDateRange(rangeFromPeriodId(periodId!)), 'common.filter.company.ALL'),
            businessUnitName: getOrDefault(businessUnit, 'dashboard.company.all-departments'),
            employeeSegment: getOrDefault(this.label(`dashboard.filter.employee-segment.${employeeSegment}`), 'common.service-length.ALL'),
            role: getOrDefault(role === 'ALL' ? this.label('common.roles.all') : role, 'common.filter.company.ALL'),
            serviceLength: getOrDefault(this.label(`common.service-length.${serviceLength}`), 'common.service-length.'),
            question: getOrDefault(question === 'ALL' ? this.label('common.questions.all') : question, 'common.filter.company.ALL'),
            tag: getOrDefault(tag === 'ALL' ? this.label('common.tags.all') : tag, 'common.filter.company.ALL'),
        };

        const labelMapping: Record<SummaryType, string> = {
            dashboard: 'mentor.summary-display-question',
            category: 'mentor.category-display-question',
            department: 'mentor.department-display-question',
            tag: 'mentor.tag-display-question',
            role: 'mentor.role-display-question',
            serviceLength: 'mentor.service-length-display-question',
            country: 'mentor.country-display-question',
            feedback: 'mentor.feedback-display-question',
        };

        return summaryType && labelMapping[summaryType]
            ? this.label(labelMapping[summaryType], commonData)
            : 'undefined';
    }

    private getQuestionIdsToTranslateComments(): string[] {
        return this.payload!.feedback!.questionFeedback
            .map((questionFeedback) => questionFeedback.catalogQuestionId);
    }

    @action
    private setCategories(): void {
        this.categories = this.categoriesStore.categories;
    }

    @action
    private setIsTranslationButtonLoading(isLoading: boolean): void {
        this.isTranslationButtonLoading = isLoading;
    }

    private getRedirectToDashboardUrl(): string {
        const url = new URL(window.location.href);

        if (url.searchParams.has('token')) {
            url.searchParams.delete('token');
        }

        return url.toString();
    }

    private async loadDashboard(employeeId: string, businessUnitId?: string): Promise<TeamResultsPayload> {
        const businessUnitQueryParam: Record<string, string> = {};

        if (businessUnitId !== undefined) {
            businessUnitQueryParam['business-unit'] = businessUnitId;
            this.variables.businessUnitId = businessUnitId;
        }
        const businessUnitQueryParamString = new URLSearchParams(businessUnitQueryParam).toString();

        await this.userStore.loadUser();
        await this.categoriesStore.refreshPayload();
        this.setCategories();

        return this.api.get<TeamResultsPayload>(`/dashboard/employee/${employeeId}?${businessUnitQueryParamString}`);
    }

    private initiateLogin(token: string | undefined, tenantId: string | undefined, loadPayload: () => Promise<void>): void {
        if (token !== undefined || tenantId !== undefined) {
            this.login(token, tenantId,loadPayload);
        } else {
            this.load(async () => {
                this.variables = new TeamResultsVariables();
                let tokenToBeChecked;

                if (Capacitor.getPlatform() === 'web') {
                    tokenToBeChecked = sessionStorage.getItem('session-token') !== undefined || sessionStorage.getItem('session-token') !== null ?
                        sessionStorage.getItem('session-token') : undefined;
                } else {
                    tokenToBeChecked = localStorage.getItem('session-token') !== undefined || localStorage.getItem('session-token') !== null ?
                        localStorage.getItem('session-token') : undefined;
                }

                if (tokenToBeChecked === undefined || tokenToBeChecked === null || SessionStore.isExpired(SessionStore.parseJwtPayload(tokenToBeChecked))) {
                    this.variables.expired = true;

                    return;
                }
                await loadPayload();
                await this.userStore.loadUser();
            });
        }
    }

    private login(token: string | undefined, tenantId: string | undefined, loadPayload: () => Promise<void>): void {
        this.load(async () => {
            this.variables = new TeamResultsVariables();
            if (token !== undefined) {
                if (SessionStore.isExpired(SessionStore.parseJwtPayload(token))) {
                    this.variables.expired = true;

                    return;
                }
            }
            await this.sessionStore.externalTokenLogin(token, tenantId);
            await loadPayload();
            await this.userStore.loadUser();
        });
    }
}

interface TeamResultsPayload {
    filter: Filter;
    overview?: OverviewCard;
    feedback?: FeedbackCard;
    categories?: CategoryCard;
    company?: CompanyCard;
    locations?: LocationCard;
    heatmap?: HeatmapCard;
    questions?: QuestionCards;
    adHocQuestions?: QuestionCards;
    tooltip: TooltipPayload;
    lengthsOfService?: LengthOfServiceCard;
    roles?: RolesCard;
    tags?: TagsCard;
    weeklyRetro?: WeeklyRetroCard[];
}

export interface WeeklyRetroCard {
    id: string;
    businessUnitName: string;
    weekStart: string;
    weekEnd: string;
    analysis: string;
    sourceLanguage: string;
}

export interface Filter {
    categories: FilterOption[];
    businessUnits: FilterOption[];
    employeeSegment: FilterOption[];
    compareTo: FilterOption[];
    periods: FilterOption[];
    locations?: FilterOption[];
    roles?: FilterOption[];
    lengthOfService?: FilterOption[];
    tags?: FilterOption[];
    questionId?: FilterOption[];
}

export interface FilterOption {
    id: string;
    selected: boolean;
    label?: string;
    periodTo?: string
    periodLength?: number;
    count?: number
}

interface TooltipPayload {
    period: Period;
    businessUnitName: string;
    compareToName: string;
}

export interface OverviewCard {
    total: Datapoint;
    chart: Chartpoint[];
}

interface HeatmapCard {
    rootRow: HeatmapRow;
    categories: string[];
    focusedCategory?: string;
    isMatrixTeam: boolean
}

export interface HeatmapRow {
    businessUnitName: string;
    focused: boolean;
    cells: HeatmapCell[];
    children: HeatmapRow[];
}

interface HeatmapCell {
    category: string;
    datapoint: Datapoint;
}

export interface QuestionCards {
    questions: QuestionCard[]
}

export interface QuestionCard {
    questionText: string,
    catalogQuestionId: string,
    categoryId: string,
    barpoint: Barpoint,
    feedbackAnswers: AnswerWithCommentOnQuestionCard[],
    notEnoughDataDatapoint: boolean;
    notEnoughDataCompareToDatapoint: boolean;
}

export interface AnswerWithCommentOnQuestionCard {
    text: string,
    answer?: string;
    hasSeen: boolean,
    questionId: string,
    questionBusinessUnitId: string
}

export interface ManagerHasSeenComment {
    hasSeen: boolean,
    questionId: string
}

interface CompanyCard {
    rootBusinessUnitData: BusinessUnitData;
    isMatrixTeam: boolean
}

export interface BusinessUnitData {
    id: string;
    title: string;
    barpoint: Barpoint;
    scoreChange: number;
    focused: boolean;
    children: BusinessUnitData[];
}

export interface CategoryCard {
    categories: DashboardData[];
}

interface LocationCard {
    locations: DashboardData[];
}

interface LengthOfServiceCard {
    serviceLengths: DashboardData[];
}

interface RolesCard {
    roles: DashboardData[];
}

interface TagsCard {
    tags: DashboardData[];
}

export interface DashboardData {
    value: string;
    barpoint: Barpoint;
    scoreChange: number;
    focused: boolean;
}

export interface FeedbackCard {
    questionFeedback: QuestionFeedback[];
}

export interface QuestionFeedback {
    categoryId: string;
    catalogQuestionId: string;
    questionText: string;
    answers: AnswerWithComment[];
}

export interface CommentWithTranslation {
    text: string;
    textEstonian?: string;
    textEnglish?: string;
    textFinnish?: string;
    textLithuanian?: string;
    textLatvian?: string;
    textRussian?: string;
    textSwedish?: string;
    textPolish?: string;
    textJapanese?: string;
    textVietnamese?: string;
}

export interface AnswerWithComment extends CommentWithTranslation{
    text: string;
    textEnglish?: string,
    textEstonian?: string,
    textFinnish?: string,
    textLatvian?: string,
    textLithuanian?: string,
    textRussian?: string,
    textSwedish?: string,
    textPolish?: string,
    textJapanese?: string,
    textVietnamese?: string,
    answer: string;
    hasSeen: boolean,
    questionId: string,
    questionBusinessUnitId: string,
    questionBusinessUnitName: string,
    likes: ILike;
    commentCount: number;
    comments: IComment[];
}

export interface Barpoint {
    value: Datapoint;
    compareTo: Datapoint;
}

export interface Chartpoint {
    value: Datapoint;
    compareTo: Datapoint;
    period: Period;
}

export interface Datapoint {
    prepared: number;
    asked: number;
    answered: number;
    scorePercentage: number;
    participationPercentage: number;
    onlyFreeTextAnswers?: boolean;
    distributionCounts?: DistributionCounts
}

export interface Period {
    from: string;
    to: string;
}

export interface ToggleLikeResponse {
    result: 'LIKED'| 'UNLIKED',
    likeCount: number;
}

class TeamResultsVariables {
    @observable
    context?: DashboardContext = undefined;
    @observable
    openTab?: DashboardPageTab = undefined;
    @observable
    categoryChoice?: string = undefined;
    @observable
    periodId?: string = undefined;
    @observable
    businessUnitId?: string = undefined;
    @observable
    compareToBusinessUnitId?: string = undefined;
    @observable
    employeeSegment?: string = undefined;
    @observable
    openModal?: TeamDashboardModal = undefined;
    @observable
    locationChoice?: string = undefined;
    @observable
    role?: string = undefined;
    @observable
    lengthOfService?: string = undefined;
    @observable
    tag?: string = undefined;
    @observable
    shownQuestionCardsAmount: number = 21;
    @observable
    expired: boolean = false;
    @observable
    isCustomDate: boolean = false;
    @observable
    questionId?: string = undefined;
    @observable
    validationReason?: string = undefined;
    @observable
    showValidation?: boolean = false;
    @observable
    questionWithComment?: IAnswerProps = undefined;

    constructor(expired: boolean = false) {
        this.expired = expired;
        makeObservable(this);
    }
}
